import { SupportedLanguage } from "@/config/config";

// SEO Constants
const SEO_CONSTANTS = {
  DOMAIN: "https://www.efb.eg",
  TWITTER_HANDLE: "@EGYFOODBANK",
  DEFAULT_IMAGE: "https://minio-coh.efb.eg/efb/documents/COH.svg",
  LOCALES: {
    ar: "ar_EG",
    en: "en_US",
  } as const,
} as const;

export interface SchemaConfig {
  pageKey: string;
  language: SupportedLanguage;
  url: string;
  title: string;
  description: string;
  image?: string;
  dynamicData?: Record<string, any>;
}

/**
 * Generates Organization schema markup
 */
function generateOrganizationSchema(language: SupportedLanguage): object {
  const siteName = language === "ar" ? "بنك الطعام المصري" : "Egyptian Food Bank";
  const description =
    language === "ar"
      ? "منظمة غير حكومية مصرية تهدف إلى مكافحة الجوع وتوفير الأمن الغذائي للمحتاجين في مصر"
      : "Egyptian non-governmental organization dedicated to fighting hunger and providing food security to those in need in Egypt";

  return {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: siteName,
    alternateName: language === "ar" ? "Egyptian Food Bank" : "بنك الطعام المصري",
    url: SEO_CONSTANTS.DOMAIN,
    logo: {
      "@type": "ImageObject",
      url: SEO_CONSTANTS.DEFAULT_IMAGE,
      width: 400,
      height: 400,
    },
    description: description,
    foundingDate: "2006",
    address: {
      "@type": "PostalAddress",
      addressCountry: "EG",
      addressLocality: language === "ar" ? "القاهرة" : "Cairo",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: `${SEO_CONSTANTS.DOMAIN}/${language}/contact`,
    },
    sameAs: [
      "https://x.com/EGYFOODBANK",
      "https://www.instagram.com/EGYFOODBANK/",
      "https://web.facebook.com/EgyFoodBank?_rdc=1&_rdr",
      "https://www.youtube.com/@egyfoodbank",
      "https://www.linkedin.com/company/egyptian-food-bank/",
    ],
  };
}

/**
 * Generates WebSite schema markup
 */
function generateWebSiteSchema(language: SupportedLanguage): object {
  const siteName = language === "ar" ? "بنك الطعام المصري" : "Egyptian Food Bank";

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: SEO_CONSTANTS.DOMAIN,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SEO_CONSTANTS.DOMAIN}/${language}/programs?search={search_term_string}`,
      },
      "query-input": {
        "@type": "PropertyValueSpecification",
        valueRequired: "http://schema.org/True",
        valueName: "search_term_string",
        description: language === "ar" ? "ابحث في الموقع..." : "Search the site...",
      },
    },
  };
}

/**
 * Generates Article schema markup for blog posts, fatawa, etc.
 */
function generateArticleSchema(config: SchemaConfig): object {
  const { url, title, description, image, dynamicData } = config;

  // Format dates to YYYY-MM-DD format
  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return new Date().toISOString().split("T")[0];
    }
    try {
      return new Date(dateString).toISOString().split("T")[0];
    } catch {
      return new Date().toISOString().split("T")[0];
    }
  };

  // Build author object(s) - support single author or array of authors
  let author: any;
  if (
    dynamicData?.authors &&
    Array.isArray(dynamicData.authors) &&
    dynamicData.authors.length > 0
  ) {
    // If multiple authors, use array; if single, use object
    if (dynamicData.authors.length === 1) {
      const authorData = dynamicData.authors[0];
      author = {
        name: authorData.name || "Egyptian Food Bank",
        ...(authorData.url && { url: authorData.url }),
      };
    } else {
      author = dynamicData.authors.map((authorData: any) => ({
        name: authorData.name || "Egyptian Food Bank",
        ...(authorData.url && { url: authorData.url }),
      }));
    }
  } else {
    // Fallback to organization
    author = {
      name: "Egyptian Food Bank",
    };
  }

  // Build publisher with logo
  const publisher = {
    "@type": "Organization",
    name: config.language === "ar" ? "بنك الطعام المصري" : "Egyptian Food Bank",
    logo: {
      "@type": "ImageObject",
      url: SEO_CONSTANTS.DEFAULT_IMAGE,
    },
  };

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    image: image || SEO_CONSTANTS.DEFAULT_IMAGE,
    author: author,
    ...(dynamicData?.editor && { editor: dynamicData.editor }),
    ...(dynamicData?.genre && { genre: dynamicData.genre }),
    publisher: publisher,
    url: url,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    datePublished: formatDate(dynamicData?.datePublished),
    dateModified: formatDate(dynamicData?.dateModified || dynamicData?.datePublished),
    description: description,
    ...(dynamicData?.articleBody && { articleBody: dynamicData.articleBody }),
  };
}

/**
 * Generates FAQ schema markup
 */
function generateFAQSchema(faqs: Array<{ question: string; answer: string }>): object {
  const mainEntity = faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: mainEntity,
  };
}

/**
 * Generates Service schema markup for program pages
 */
function generateServiceSchema(config: SchemaConfig): object {
  const { url, title, description, image } = config;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: title,
    description: description,
    url: url,
    image: image || SEO_CONSTANTS.DEFAULT_IMAGE,
    provider: generateOrganizationSchema(config.language),
  };
}

/**
 * Generates ContactPage schema markup
 */
function generateContactPageSchema(config: SchemaConfig): object {
  const { url, title, description } = config;
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(config.language),
  };
}

/**
 * Generates AboutPage schema markup
 */
function generateAboutPageSchema(config: SchemaConfig): object {
  const { url, title, description } = config;
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(config.language),
  };
}

/**
 * Generates CollectionPage schema markup for listing pages
 */
function generateCollectionPageSchema(config: SchemaConfig): object {
  const { url, title, description } = config;
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(config.language),
  };
}

/**
 * Generates WebPage schema markup for general pages
 */
function generateWebPageSchema(config: SchemaConfig): object {
  const { url, title, description } = config;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description: description,
    url: url,
    publisher: generateOrganizationSchema(config.language),
  };
}

/**
 * Generates Event schema markup for challenge pages
 */
function generateEventSchema(config: SchemaConfig, dynamicData?: Record<string, any>): object {
  const { url, title, description, image } = config;
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: title,
    description: description,
    image: image || SEO_CONSTANTS.DEFAULT_IMAGE,
    url: url,
    organizer: generateOrganizationSchema(config.language),
    startDate: dynamicData?.startDate || new Date().toISOString(),
    endDate: dynamicData?.endDate,
  };
}

/**
 * Generates JobPosting schema markup for join-us page
 */
function generateJobPostingSchema(config: SchemaConfig): object {
  const { url, title, description, dynamicData } = config;

  // Create Organization object specifically for hiringOrganization (not NGO)
  const hiringOrg = {
    "@type": "Organization",
    name: config.language === "ar" ? "بنك الطعام المصري" : "Egyptian Food Bank",
    alternateName: config.language === "ar" ? "Egyptian Food Bank" : "بنك الطعام المصري",
    url: SEO_CONSTANTS.DOMAIN,
    logo: {
      "@type": "ImageObject",
      url: SEO_CONSTANTS.DEFAULT_IMAGE,
      width: 400,
      height: 400,
    },
  };

  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: title,
    description: description,
    url: url,
    hiringOrganization: hiringOrg, // Fixed: Now uses Organization type
    datePosted: dynamicData?.datePosted || new Date().toISOString().split("T")[0], // CRITICAL: Added in YYYY-MM-DD format
    employmentType: "FULL_TIME",
    validThrough: dynamicData?.validThrough, // RECOMMENDED: Job closing date
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: "EG",
        addressLocality: config.language === "ar" ? "القاهرة" : "Cairo",
        streetAddress: dynamicData?.streetAddress, // RECOMMENDED
        addressRegion: dynamicData?.addressRegion || "Cairo", // RECOMMENDED
        postalCode: dynamicData?.postalCode, // RECOMMENDED
      },
    },
    baseSalary: dynamicData?.baseSalary
      ? {
          // RECOMMENDED
          "@type": "MonetaryAmount",
          currency: "EGP",
          value: {
            "@type": "QuantitativeValue",
            value: dynamicData.baseSalary.value,
            unitText: dynamicData.baseSalary.unitText || "MONTH",
          },
        }
      : undefined,
  };
}

/**
 * Generates BreadcrumbList schema markup
 * @param breadcrumbs Array of { name: string, url: string } objects
 */
function generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>): object {
  const itemListElement = breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@id": crumb.url,
      name: crumb.name,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: itemListElement,
  };
}

/**
 * Main function to generate appropriate schema markup based on page type
 */
export function generateSchemaMarkup(config: SchemaConfig): string {
  const { pageKey, dynamicData } = config;
  let schema: object[] = [
    generateOrganizationSchema(config.language),
    generateWebSiteSchema(config.language),
  ];

  // Add breadcrumbs if provided (only on pages that have breadcrumbs)
  if (
    dynamicData?.breadcrumbs &&
    Array.isArray(dynamicData.breadcrumbs) &&
    dynamicData.breadcrumbs.length > 0
  ) {
    schema.push(generateBreadcrumbSchema(dynamicData.breadcrumbs));
  }

  switch (pageKey) {
    // Article pages
    case "blog-detail":
    case "fatwa-detail":
      schema.push(generateArticleSchema(config));
      break;

    // Service pages
    case "program-detail":
      schema.push(generateServiceSchema(config));
      break;

    // FAQ page
    case "faq":
      if (dynamicData?.faqs && Array.isArray(dynamicData.faqs) && dynamicData.faqs.length > 0) {
        schema.push(generateFAQSchema(dynamicData.faqs));
      }
      break;

    // Contact page
    case "contact":
      schema.push(generateContactPageSchema(config));
      break;

    // About page
    case "about":
      schema.push(generateAboutPageSchema(config));
      break;

    // Listing pages (CollectionPage)
    case "home":
    case "programs":
    case "blog":
    case "fatwa":
      schema.push(generateCollectionPageSchema(config));
      break;

    // Challenge detail (Event schema)
    case "challenge-detail":
      schema.push(generateEventSchema(config, dynamicData));
      break;

    // Join us page (JobPosting schema)
    case "join-us":
      schema.push(generateJobPostingSchema(config));
      break;

    // General pages (WebPage schema)
    case "policy":
    case "donation-flow":
    case "thank-you":
    case "error-404":
    case "error-500":
    case "circle":
    case "challenge":
      schema.push(generateWebPageSchema(config));
      break;

    default:
      // For any other pages, add WebPage schema as fallback
      schema.push(generateWebPageSchema(config));
      break;
  }

  return JSON.stringify(schema);
}
